import { Component, OnInit, inject, Inject } from '@angular/core';
import { ClientsService } from '../../core/services/clients.service';
import { Client, CreateClientDto, UpdateClientDto } from '../../core/models/client.model';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule, DatePipe } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

@Component({
  selector: 'app-clients',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    TranslateModule,
    DatePipe
  ],
  template: `
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ 'clients.title' | translate }}</h1>
      </div>
      <button mat-raised-button color="primary" (click)="openCreate()" class="create-button">
        <mat-icon>add</mat-icon>
        {{ 'clients.create' | translate }}
      </button>
    </div>

    <div class="table-container" *ngIf="data.length">
      <table mat-table [dataSource]="data" class="clients-table">
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef>{{ 'clients.name' | translate }}</th>
          <td mat-cell *matCellDef="let c">{{ c.name }}</td>
        </ng-container>
        <ng-container matColumnDef="activeFrom">
          <th mat-header-cell *matHeaderCellDef>{{ 'clients.activeFrom' | translate }}</th>
          <td mat-cell *matCellDef="let c">{{ c.activeFrom | date }}</td>
        </ng-container>
        <ng-container matColumnDef="active">
          <th mat-header-cell *matHeaderCellDef>{{ 'clients.active' | translate }}</th>
          <td mat-cell *matCellDef="let c">
            <span class="status-badge" [ngClass]="c.active ? 'active' : 'inactive'">
              {{ c.active ? ('common.active' | translate) : ('common.inactive' | translate) }}
            </span>
          </td>
        </ng-container>
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="table-actions">{{ 'common.actions' | translate }}</th>
          <td mat-cell *matCellDef="let c" class="table-actions">
            <button mat-icon-button (click)="openEdit(c)" class="action-button edit-button">
              <mat-icon svgIcon="edit-custom"></mat-icon>
            </button>
            <button mat-icon-button (click)="confirmDelete(c)" class="action-button delete-button">
              <mat-icon svgIcon="delete-custom"></mat-icon>
            </button>
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="cols"></tr>
        <tr mat-row *matRowDef="let row; columns: cols;" class="table-row"></tr>
      </table>
    </div>

    <div class="empty-state" *ngIf="!data.length">
      <mat-icon>people_outline</mat-icon>
      <p>{{ 'clients.noData' | translate }}</p>
    </div>
  `,
  styles: [`
    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }

    .page-title {
      margin: 0;
      font-size: 28px;
      font-weight: 600;
      color: #1F2937;
    }

    .create-button {
      height: 42px;
      border-radius: 4px;
      font-weight: 600;
      background-color: #3B82F6 !important;
      color: #FFFFFF !important;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
      transition: all 0.2s ease;
    }

    .create-button:hover {
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35);
      transform: translateY(-1px);
    }

    .create-button mat-icon {
      margin-right: 8px;
    }

    .table-container {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      border: 1px solid #E5E7EB;
    }

    .clients-table {
      width: 100%;
      background: white;
    }

    .clients-table th {
      background: #FFFFFF;
      color: #4B5563;
      font-weight: 600;
      padding: 10px 16px !important;
      border-bottom: 1px solid #D1D5DB;
    }

    .clients-table td {
      padding: 10px 16px !important;
      color: #1F2937;
      font-size: 14px;
      border-bottom: 1px solid #E5E7EB;
    }

    /* Rows remain white without zebra striping per guideline */

    .status-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    .status-icon.active {
      color: #10B981;
    }

    .status-icon.inactive {
      color: #EF4444;
    }

    .actions-header {
      text-align: right !important;
    }

    .actions-cell {
      text-align: right !important;
    }

    .action-button {
      margin-left: 2px;
      transition: all 0.2s ease;
    }

    .action-button.edit-button { color: #000000 !important; }
    .action-button.edit-button:hover { background-color: rgba(0, 0, 0, 0.06); }
    .action-button.delete-button { color: #000000 !important; }
    .action-button.delete-button:hover { background-color: rgba(0, 0, 0, 0.06); }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 80px 20px;
      background: white;
      border-radius: 10px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border: 1px solid #E5E7EB;
    }

    .empty-state mat-icon {
      font-size: 64px;
      width: 64px;
      height: 64px;
      color: #D1D5DB;
      margin-bottom: 16px;
    }

    .empty-state p {
      color: #6B7280;
      font-size: 16px;
      margin: 0;
    }
  `]
})
export class ClientsComponent implements OnInit {
  private service = inject(ClientsService);
  private dialog = inject(MatDialog);
  private snack = inject(MatSnackBar);
  private t = inject(TranslateService);

  cols = ['name', 'activeFrom', 'active', 'actions'];
  data: Client[] = [];

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.service.getAll().subscribe({
      next: (list) => (this.data = list),
      error: () => this.snack.open(this.t.instant('common.loadError'), this.t.instant('common.close'), { duration: 3000 })
    });
  }

  openCreate() {
    const ref = this.dialog.open(ClientDialogComponent, { data: null });
    ref.afterClosed().subscribe((dto?: CreateClientDto) => {
      if (!dto) return;
      this.service.create(dto).subscribe({
        next: () => { this.snack.open(this.t.instant('clients.created'), this.t.instant('common.close'), { duration: 2000 }); this.load(); },
        error: (e) => this.snack.open(e?.error?.message || this.t.instant('common.saveError'), this.t.instant('common.close'), { duration: 3000 })
      });
    });
  }

  openEdit(client: Client) {
    const ref = this.dialog.open(ClientDialogComponent, { data: client });
    ref.afterClosed().subscribe((dto?: UpdateClientDto) => {
      if (!dto) return;
      this.service.update(client.id, dto).subscribe({
        next: () => { this.snack.open(this.t.instant('clients.updated'), this.t.instant('common.close'), { duration: 2000 }); this.load(); },
        error: (e) => this.snack.open(e?.error?.message || this.t.instant('common.saveError'), this.t.instant('common.close'), { duration: 3000 })
      });
    });
  }

  confirmDelete(client: Client) {
    const yes = confirm(this.t.instant('clients.confirmDelete', { name: client.name }));
    if (!yes) return;
    this.service.delete(client.id).subscribe({
      next: () => { this.snack.open(this.t.instant('clients.deleted'), this.t.instant('common.close'), { duration: 2000 }); this.load(); },
      error: (e) => this.snack.open(e?.error?.message || this.t.instant('common.deleteError'), this.t.instant('common.close'), { duration: 3000 })
    });
  }
}

@Component({
  selector: 'app-client-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatDialogModule,
    TranslateModule
  ],
  template: `
    <h2 mat-dialog-title>{{ data ? ('clients.edit' | translate) : ('clients.create' | translate) }}</h2>
    <form [formGroup]="form" (ngSubmit)="save()" style="padding: 0 24px 16px 24px; display:block; width:640px; max-width:100%;">
      <div style="display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap:12px;">
        <mat-form-field appearance="outline">
          <mat-label>{{ 'clients.name' | translate }}</mat-label>
          <input matInput formControlName="name" required>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>{{ 'clients.activeFrom' | translate }}</mat-label>
          <input matInput [matDatepicker]="picker" formControlName="activeFrom" required>
          <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>{{ 'clients.registrationNo' | translate }}</mat-label>
          <input matInput formControlName="registrationNo">
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>{{ 'clients.taxId' | translate }}</mat-label>
          <input matInput formControlName="taxId">
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>{{ 'clients.address' | translate }}</mat-label>
          <input matInput formControlName="address">
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>{{ 'clients.city' | translate }}</mat-label>
          <input matInput formControlName="city">
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>{{ 'clients.contactPerson' | translate }}</mat-label>
          <input matInput formControlName="contactPerson">
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>{{ 'clients.phone' | translate }}</mat-label>
          <input matInput formControlName="phone">
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>{{ 'clients.email' | translate }}</mat-label>
          <input matInput type="email" formControlName="email">
        </mat-form-field>
        <mat-checkbox style="align-self:center;" formControlName="active">{{ 'clients.active' | translate }}</mat-checkbox>
      </div>
      <mat-form-field style="width:100%; margin-top:12px;" appearance="outline">
        <mat-label>{{ 'common.notes' | translate }}</mat-label>
        <textarea matInput rows="3" formControlName="notes"></textarea>
      </mat-form-field>
      <div style="margin-top:12px; display:flex; gap:8px; justify-content:flex-end;">
        <button mat-stroked-button mat-dialog-close>{{ 'common.cancel' | translate }}</button>
        <button mat-raised-button color="primary" type="submit" [disabled]="form.invalid">{{ 'common.save' | translate }}</button>
      </div>
    </form>
  `
})
export class ClientDialogComponent {
  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ClientDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Client | null,
  ) {
    if (data) {
      this.form.patchValue({
        name: data.name,
        activeFrom: new Date(data.activeFrom),
        active: data.active
      });
    }
  }

  form = this.fb.group({
    name: ['', [Validators.required]],
    activeFrom: [new Date(), [Validators.required]],
    active: [true],
    registrationNo: [''],
    taxId: [''],
    address: [''],
    city: [''],
    contactPerson: [''],
    phone: [''],
    email: [''],
    notes: ['']
  });

  save() {
    if (this.form.invalid) return;
    const v = this.form.value;
    const dto: any = {
      name: v.name!,
      activeFrom: (v.activeFrom as Date).toISOString(),
      active: !!v.active,
      registrationNo: v.registrationNo || null,
      taxId: v.taxId || null,
      address: v.address || null,
      city: v.city || null,
      contactPerson: v.contactPerson || null,
      phone: v.phone || null,
      email: v.email || null,
      notes: v.notes || null
    };
    this.dialogRef.close(dto);
  }
}
